import React from 'react';

import {
  fetchContentTree,
  getCachedContentTree,
  getStaleContentTree,
  subscribeToCmsChanges
} from '../api/cms';
import { useLanguage } from '../context/LanguageContext';
import { getRootMenuNodes } from '../utils/menu';

export function useMenuTree(resource = 'menus', options = {}) {
  const { currentLanguageCode } = useLanguage();
  const enabled = options.enabled !== false;
  const shouldRevalidate = options.revalidate !== false;
  const cachedMenus = React.useMemo(() => {
    if (!enabled) {
      return null;
    }

    const nextMenus = getCachedContentTree(resource);
    return nextMenus ? getRootMenuNodes(nextMenus) : null;
  }, [enabled, resource]);
  // Oldingi tashrifdan qolgan menyu: darhol ko'rsatiladi, fonda yangilanadi.
  const staleMenus = React.useMemo(() => {
    if (!enabled || cachedMenus) {
      return null;
    }

    const nextMenus = getStaleContentTree(resource);
    return nextMenus ? getRootMenuNodes(nextMenus) : null;
  }, [cachedMenus, enabled, resource]);
  const [menus, setMenus] = React.useState(() => cachedMenus || staleMenus || []);
  const [loading, setLoading] = React.useState(() => enabled && !cachedMenus && !staleMenus);

  React.useEffect(() => {
    let cancelled = false;

    const load = async (forceRefresh = false) => {
      if (!enabled) {
        setMenus([]);
        setLoading(false);
        return;
      }

      try {
        if (cachedMenus && !forceRefresh) {
          setMenus(cachedMenus);
          setLoading(false);
          if (!shouldRevalidate) {
            return;
          }
        }

        setLoading((current) => current || !staleMenus);
        const nextMenus = await fetchContentTree(resource, {}, {
          // Reuse the cached menu tree while the revision listener watches for changes.
          force: forceRefresh
        });

        if (!cancelled) {
          setMenus(getRootMenuNodes(nextMenus));
        }
      } catch (error) {
        // Tarmoq xatosida mavjud menyuni o'chirib yubormaymiz.
        console.error(`Failed to load ${resource} tree:`, error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    const unsubscribe = shouldRevalidate
      ? subscribeToCmsChanges(() => {
          load(true);
        })
      : () => {};

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [cachedMenus, enabled, resource, shouldRevalidate, staleMenus]);

  return {
    menus,
    loading,
    currentLanguageCode
  };
}
